import { useCallback, useState } from "react";
import {
    ApiQuery,
    Filter,
    FilterOperators,
    PaginationState,
    QueryResponse,
} from "~/modules/filters/types";
import { api } from "~/utils/api";
import { deobfuscate } from "~/utils/obfuscate";
import { Asset } from "~/modules/assets/types";
import { getScreenerAssets } from "~/modules/asset_screener/lib/getScreenerAssets";
import type { ScreenerAsset } from "~/modules/asset_screener/types";
import { ASSET_SCREENER_BASE_FILTER } from "../../consts";

// // // //

const selectAssetQueryResponse = (data: unknown) =>
    deobfuscate(data as string) as QueryResponse<Asset>;

function withBaseFilter(apiQuery: ApiQuery): ApiQuery {
    return {
        ...apiQuery,
        filter: {
            operator: FilterOperators.And,
            filters: apiQuery.filter
                ? [ASSET_SCREENER_BASE_FILTER, apiQuery.filter]
                : [ASSET_SCREENER_BASE_FILTER],
        } as Filter,
    };
}

// Caches the ScreenerAsset conversion per page response, so already-loaded
// pages aren't re-converted every time another page loads
const screenerAssetsByResponse = new WeakMap<
    QueryResponse<Asset>,
    ScreenerAsset[]
>();

function getPageScreenerAssets(response: QueryResponse<Asset>) {
    let screenerAssets = screenerAssetsByResponse.get(response);
    if (!screenerAssets) {
        screenerAssets = getScreenerAssets(response.results);
        screenerAssetsByResponse.set(response, screenerAssets);
    }
    return screenerAssets;
}

// // // //

/**
 * AssetScreenerListProvider
 * Fetches the asset screener list for the given query, page by page, and
 * passes the concatenated assets plus infinite-scroll state to its children.
 * Renders initialState as the first page until the client query loads.
 */
export function AssetScreenerListProvider(props: {
    initialState: QueryResponse<Asset>;
    apiQuery: ApiQuery;
    onChangeQuery: (updatedQuery: ApiQuery) => void;
    children: (renderProps: {
        assets: Asset[];
        screenerAssets: ScreenerAsset[];
        isFetching: boolean;
        hasNextPage: boolean;
        loadNextPage: () => void;
        pagination: PaginationState | undefined;
    }) => React.ReactNode;
}) {
    const { initialState, apiQuery } = props;
    const { perPage } = apiQuery.pagination;

    // Number of pages loaded beyond the first - tied to the query (minus
    // pagination) so it resets whenever the filters / sort change
    const queryKey = JSON.stringify({ ...apiQuery, pagination: undefined });
    const [extraPages, setExtraPages] = useState({ queryKey, count: 0 });
    const extraPageCount =
        extraPages.queryKey === queryKey ? extraPages.count : 0;

    // initialState is the first page for the query rendered on mount, so it's
    // only valid as a stand-in while that query is still active
    const [initialQueryKey] = useState(queryKey);
    const isInitialQuery = queryKey === initialQueryKey;

    // First page uses keepPreviousData so the previous results stay on screen
    // while a new query loads (useQueries doesn't support keepPreviousData)
    const firstPageReq = api.assets.query.useQuery(
        {
            query: withBaseFilter({
                ...apiQuery,
                pagination: { page: 1, perPage },
            }),
        },
        {
            refetchOnWindowFocus: false,
            keepPreviousData: true,
            select: selectAssetQueryResponse,
        },
    );

    // One query per additional page - react-query caches each page, so
    // previously loaded pages don't need to be stashed separately
    const extraPageReqs = api.useQueries((t) =>
        Array.from({ length: extraPageCount }, (_, index) =>
            t.assets.query(
                {
                    query: withBaseFilter({
                        ...apiQuery,
                        pagination: { page: index + 2, perPage },
                    }),
                },
                {
                    refetchOnWindowFocus: false,
                    select: selectAssetQueryResponse,
                },
            ),
        ),
    );

    // First page falls back to initialState until the query has loaded. After
    // the query changes, it stays on screen like keepPreviousData would
    const firstPage = firstPageReq.data ?? initialState;
    const isPreviousFirstPage =
        firstPageReq.isPreviousData || (!firstPageReq.data && !isInitialQuery);

    // Concatenate pages in order, stopping at the first page that hasn't loaded
    const loadedResponses: QueryResponse<Asset>[] = [firstPage];
    if (!isPreviousFirstPage) {
        for (const req of extraPageReqs) {
            if (!req.data) break;
            loadedResponses.push(req.data as QueryResponse<Asset>);
        }
    }
    const assets = loadedResponses.flatMap((response) => response.results);
    const screenerAssets = loadedResponses.flatMap(getPageScreenerAssets);

    const isFetching =
        firstPageReq.isFetching || extraPageReqs.some((req) => req.isFetching);
    const loadedPageCount = extraPageCount + 1;
    const hasNextPage =
        !isPreviousFirstPage &&
        loadedResponses.length === loadedPageCount &&
        firstPage.pagination.pageCount > loadedPageCount;

    // Re-created whenever a fetch settles, so the tracker re-observes and keeps
    // loading pages while it's still in view
    const loadNextPage = useCallback(() => {
        if (!hasNextPage || isFetching) return;
        setExtraPages({ queryKey, count: extraPageCount + 1 });
    }, [hasNextPage, isFetching, queryKey, extraPageCount]);

    return props.children({
        assets,
        screenerAssets,
        isFetching,
        hasNextPage,
        loadNextPage,
        pagination: firstPage.pagination,
    });
}
